// ============================================================
// AssistantPage — l'assistant Mertens, en page entière.
// ------------------------------------------------------------
// Les conversations à gauche, le fil à droite. Pour l'usage quotidien,
// c'est le PANNEAU du devis qui compte (il connaît le devis ouvert) ;
// cette page sert aux questions qui ne portent pas sur un devis — « quels
// devis attendent depuis dix jours ? », « où perd-on du temps ? ».
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useSession } from '../../lib/session.jsx';
import CarteProposition from './CarteProposition.jsx';
import Button from '../../ui/Button.jsx';
import { Card } from '../../ui/Card.jsx';
import api from '../../lib/api.js';
import Conversation from './Conversation.jsx';
import './assistant.css';
import { formatDate } from '../../lib/format.js';

const EXEMPLES = [
  'Quels devis sont envoyés et sans réponse depuis plus de dix jours ?',
  'Combien de devis gagnés et perdus depuis le 1er septembre ?',
  'Que dit l’écran Apprentissage ce mois-ci ?',
];

export function AssistantPage() {
  const [conversations, setConversations] = useState([]);
  // `?conversation=12` : le lien « voir la conversation » d'une règle.
  const [params] = useSearchParams();
  const [courante, setCourante] = useState(Number(params.get('conversation')) || null);
  const { peutValiderRegles } = useSession();
  // Ce qui attend Charles ou Philippe, toutes conversations confondues :
  // une règle proposée par l'un se valide aussi depuis l'écran de l'autre.
  const [aValider, setAValider] = useState([]);
  const chargerAValider = useCallback(async () => {
    if (!peutValiderRegles) return;
    try {
      const r = await api.get('/assistant/propositions?statut=pending');
      setAValider(r.propositions ?? []);
    } catch {
      setAValider([]);
    }
  }, [peutValiderRegles]);
  useEffect(() => { chargerAValider(); }, [chargerAValider]);

  const charger = useCallback(async () => {
    try {
      const r = await api.get('/assistant/conversations');
      setConversations(r.conversations ?? []);
    } catch {
      // La liste est un confort : sans elle, on peut encore poser une question.
      setConversations([]);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  return (
    <div className="assistant-page">
      <Card className="assistant-page__liste">
        <Button variant="primary" block onClick={() => setCourante(null)}>Nouvelle conversation</Button>
        <ul className="assistant-page__conversations">
          {conversations.map((c) => (
            <li key={c.id}>
              <button type="button" aria-current={c.id === courante ? 'true' : undefined}
                className="assistant-page__conversation" onClick={() => setCourante(c.id)}>
                <span>{c.titre ?? 'Sans titre'}</span>
                <span className="field__hint">
                  {c.devis_reference ? `${c.devis_reference} · ` : ''}
                  {formatDate(c.updated_at)}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {aValider.length ? (
          <div className="assistant-page__a-valider">
            <p className="caps">{`À valider (${aValider.length})`}</p>
            {aValider.map((p) => (
              <CarteProposition key={p.id} proposition={p} onChange={chargerAValider}
                onModifier={() => setCourante(p.conversation_id)} />
            ))}
          </div>
        ) : null}
      </Card>
      <Card className="assistant-page__fil">
        <Conversation
          key={courante ?? 'nouvelle'}
          conversationId={courante}
          exemples={EXEMPLES}
          onCree={(id) => { setCourante(id); charger(); }}
        />
      </Card>
    </div>
  );
}

export default AssistantPage;
